// What the project Home says about a project, worked out from what is already known: the service list and the
// technologies listed in `.mia/project.json`. No guessing beyond names and well-known technologies.

const DATA_STORES = ['PostgreSQL', 'MongoDB', 'Redis', 'Neo4j', 'SQLite', 'MySQL', 'S3', 'MinIO'];
const BACKEND_TECH = ['Node.js', 'Node', 'Express', 'Fastify', 'Spring Boot', 'Spring', 'Java', 'Python', 'FastAPI', 'Flask', '.NET', 'ASP.NET', 'Rust', 'Go'];
const FRONTEND_TECH = ['React', 'Vue', 'Angular', 'Next.js', 'Svelte', 'Vite', 'HTML', 'CSS'];

const sameTech = (list, tech) => list.some((known) => known.toLowerCase() === String(tech).toLowerCase());

/** The data stores among a project's technologies (PostgreSQL, MongoDB…), in the order they were listed. */
export function dataStoresOf(technologies) {
  return (technologies || []).filter((tech) => sameTech(DATA_STORES, tech));
}

/** Which role a service plays, from its name: a gateway/BFF, a backend, or a frontend (the default for a service that is neither). */
export function serviceRole(key, service, technologies) {
  const name = `${key} ${service?.displayName || service?.name || ''}`.toLowerCase();
  if (/(gateway|bff)/.test(name)) { return 'gateway'; }
  if (/(api|backend|server|worker|service-)/.test(name) || /(^|\/)be(\b|-)/.test(name)) { return 'backend'; }
  if (/(app|web|ui|front|client|portal|console|site|dashboard)/.test(name)) { return 'frontend'; }
  const techs = technologies || [];
  const hasFront = techs.some((tech) => sameTech(FRONTEND_TECH, tech));
  const hasBack = techs.some((tech) => sameTech(BACKEND_TECH, tech));
  return hasBack && !hasFront ? 'backend' : 'frontend';
}

/** "React", "Express": the technology a service is most likely built with, from the project's list and its role. */
export function serviceStack(key, service, technologies) {
  const techs = technologies || [];
  const role = serviceRole(key, service, techs);
  const known = role === 'frontend' ? FRONTEND_TECH : BACKEND_TECH;
  return techs.find((tech) => sameTech(known, tech)) || null;
}

/**
 * The columns of "How it fits together": Frontends, Gateway, Backends and Data, with the empty ones left out.
 * `null` when there is nothing to map (a single service and no data store).
 */
export function buildMap(services, technologies) {
  const stores = dataStoresOf(technologies);
  if (services.length === 0 || (services.length < 2 && stores.length === 0)) { return null; }
  const nodes = { backend: [], frontend: [], gateway: [] };
  services.forEach(([key, service]) => {
    nodes[serviceRole(key, service, technologies)].push({ key, label: service.displayName || service.name || key, service });
  });
  const columns = [
    { id: 'frontend', nodes: nodes.frontend, title: 'Frontends' },
    { id: 'gateway', nodes: nodes.gateway, title: 'Gateway' },
    { id: 'backend', nodes: nodes.backend, title: 'Backends' },
    { id: 'data', nodes: stores.map((store) => ({ key: store, label: store })), title: 'Data' },
  ];
  return columns.filter((column) => column.nodes.length > 0);
}

/** `name`, or `name-1`, `name-2`… when the name is already taken (so a dropped file never replaces another). */
export function uniqueName(name, taken) {
  const used = new Set(taken);
  if (!used.has(name)) { return name; }
  const dot = name.lastIndexOf('.');
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : '';
  for (let n = 1; n < 1000; n += 1) {
    const candidate = `${stem}-${n}${ext}`;
    if (!used.has(candidate)) { return candidate; }
  }
  return `${stem}-${Date.now()}${ext}`;
}

/** The project path of a service's folder. Service keys are usually the path already (`services/app`, or `.` for the project itself). */
export function servicePath(key) {
  const name = String(key);
  return name === '.' || name.startsWith('services/') ? name : `services/${name}`;
}

/** The service the project is "about": the first frontend, else the first service. `null` when there are none. */
export function primaryService(services, technologies) {
  if (!services || services.length === 0) { return null; }
  const frontend = services.find(([key, service]) => serviceRole(key, service, technologies) === 'frontend');
  const [key, service] = frontend || services[0];
  return { key, service };
}
